import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import { ExternalIdRule, Project, UserUpload } from '../../types'
import Alert from '../../ui/Alert'
import Button from '../../ui/Button'
import Modal from '../../ui/Modal'
import ProgressBar from '../../ui/ProgressBar'
import FormWrapper from '../../ui/form/FormWrapper'
import { SingleSelect } from '../../ui/form/SingleSelect'
import UploadField from '../../ui/form/UploadField'
import './UserUploadModal.css'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const validateExternalIdValue = (
    value: string,
    rule: { type: string, pattern?: string },
): boolean => {
    if (rule.type === 'number' && (isNaN(Number(value)) || value.trim() === '')) return false
    if (rule.type === 'uuid' && !UUID_REGEX.test(value)) return false
    if (rule.type === 'email' && !EMAIL_REGEX.test(value)) return false
    if (rule.pattern && !new RegExp(rule.pattern).test(value)) return false
    return true
}

const RESERVED_FIELDS = [
    'external_id',
    'anonymous_id',
    'email',
    'phone',
    'first_name',
    'last_name',
    'timezone',
    'locale',
    'created_at',
] as const

interface UserUploadModalProps {
    open: boolean
    onClose: (success?: boolean) => void
    listId?: number
}

type Step = 'upload' | 'mapping' | 'processing'

// --- Upload Step ---

interface UploadStepProps {
    isUploading: boolean
    uploadProgress: number
    onUpload: (data: { file: FileList }) => Promise<void>
}

function UploadStep({ isUploading, uploadProgress, onUpload }: UploadStepProps) {
    const { t } = useTranslation()
    return (
        <FormWrapper<{ file: FileList }>
            onSubmit={async (data) => await onUpload(data)}
            submitLabel={t('upload')}
        >
            {form => <>
                <p>{t('upload_instructions')}</p>
                <UploadField form={form} name="file" label={t('file')} required isUploading={isUploading} />
                {isUploading && (
                    <ProgressBar
                        percent={uploadProgress}
                        label={`${t('uploading')}... ${uploadProgress}%`}
                    />
                )}
            </>}
        </FormWrapper>
    )
}

// --- Preview Navigation ---

interface PreviewNavProps {
    index: number
    total: number
    onPrev: () => void
    onNext: () => void
}

function PreviewNav({ index, total, onPrev, onNext }: PreviewNavProps) {
    if (total <= 1) return null
    return (
        <div className="upload-preview-nav">
            <button type="button" className="upload-preview-arrow" onClick={onPrev}>&#8249;</button>
            <span className="upload-preview-counter">{index + 1}/{total}</span>
            <button type="button" className="upload-preview-arrow" onClick={onNext}>&#8250;</button>
        </div>
    )
}

// --- Mapping Step ---

interface MappingStepProps {
    upload: UserUpload & { headers: string[] }
    project: Project
    mapping: Record<string, string>
    sampleData: Array<Record<string, string>>
    previewIndex: number
    isSubmitting: boolean
    onMappingChange: (csvColumn: string, targetField: string) => void
    onPreviewPrev: () => void
    onPreviewNext: () => void
    onBack: () => void
    onConfirm: () => void
}

function MappingStep({
    upload,
    project,
    mapping,
    sampleData,
    previewIndex,
    isSubmitting,
    onMappingChange,
    onPreviewPrev,
    onPreviewNext,
    onBack,
    onConfirm,
}: MappingStepProps) {
    const { t } = useTranslation()

    const hasMappedExternalId = Object.values(mapping).includes('external_id')
    const hasMappedEmail = Object.values(mapping).includes('email')
    const hasValidIdentifier = hasMappedExternalId || (project.allow_email_identification && hasMappedEmail)

    const getMappingOptions = (currentCsvColumn: string) => {
        const usedTargets = new Set(
            Object.entries(mapping)
                .filter(([col]) => col !== currentCsvColumn)
                .map(([, target]) => target),
        )
        const options: Array<{ value: string, label: string }> = []
        options.push({ value: '', label: t('add_new_field') })
        for (const field of RESERVED_FIELDS) {
            if (!usedTargets.has(field)) {
                options.push({ value: field, label: t(field) })
            }
        }
        return options
    }

    const externalIdColumn = Object.entries(mapping).find(([, v]) => v === 'external_id')?.[0]
    const externalIdSamples = externalIdColumn
        ? sampleData.map(row => row[externalIdColumn]).filter(Boolean)
        : []
    const allSamplesFail = project.external_id_rule
        && externalIdSamples.length > 0
        && externalIdSamples.every(v => !validateExternalIdValue(v, project.external_id_rule!))

    return (
        <div>
            <MappingDescription rule={project.external_id_rule} />
            {!hasValidIdentifier && (
                <Alert variant="warn" title={t('required_mapping')}>
                    {t(project.allow_email_identification ? 'external_id_or_email_required' : 'external_id_required')}
                </Alert>
            )}
            {allSamplesFail && (
                <SampleValidationError
                    rule={project.external_id_rule!}
                    column={externalIdColumn!}
                />
            )}
            <div className="upload-mapping-table-wrapper">
                <table className="upload-mapping-table">
                    <thead>
                        <tr>
                            <th>{t('csv_column')}</th>
                            <th>{t('maps_to')}</th>
                            <th>
                                <div className="upload-preview-header">
                                    <span>{t('preview')}</span>
                                    <PreviewNav
                                        index={previewIndex}
                                        total={sampleData.length}
                                        onPrev={onPreviewPrev}
                                        onNext={onPreviewNext}
                                    />
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {upload.headers.map(header => (
                            <tr key={header}>
                                <td>{header}</td>
                                <td>
                                    <SingleSelect
                                        value={mapping[header] ?? ''}
                                        onChange={(value) => onMappingChange(header, value)}
                                        options={getMappingOptions(header)}
                                        toValue={(o) => o.value}
                                        getValueKey={(v) => v}
                                        getOptionDisplay={(o) => o.label}
                                        size="small"
                                    />
                                </td>
                                <td className="upload-preview-cell">
                                    <span className="upload-preview-value">
                                        {sampleData[previewIndex]?.[header] ?? ''}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="upload-actions">
                <Button variant="secondary" onClick={onBack}>
                    {t('back')}
                </Button>
                <Button
                    onClick={onConfirm}
                    isLoading={isSubmitting}
                    disabled={!hasValidIdentifier || !!allSamplesFail}
                >
                    {t('confirm_import')}
                </Button>
            </div>
        </div>
    )
}

function MappingDescription({ rule }: { rule?: ExternalIdRule }) {
    const { t } = useTranslation()
    return (
        <p className="upload-mapping-description">
            {t('map_columns_description')}
            {rule && <> {rule.example
                ? t('external_id_rule_hint_example', {
                    type: rule.type,
                    example: rule.example,
                })
                : t('external_id_rule_hint', {
                    type: rule.type,
                })
            }</>}
        </p>
    )
}

function SampleValidationError({ rule, column }: { rule: ExternalIdRule, column: string }) {
    const { t } = useTranslation()
    return (
        <Alert variant="error" title={t('external_id_sample_error')}>
            {rule.example
                ? t('external_id_sample_error_description_example', {
                    column,
                    type: rule.type,
                    example: rule.example,
                })
                : t('external_id_sample_error_description', {
                    column,
                    type: rule.type,
                })
            }
        </Alert>
    )
}

// --- Processing Step ---

interface ProcessingStepProps {
    upload: UserUpload
    onClose: (success?: boolean) => void
}

function ProcessingStep({ upload, onClose }: ProcessingStepProps) {
    const { t } = useTranslation()

    const progressPercent = upload.row_count > 0
        ? Math.round((upload.processed_count / upload.row_count) * 100)
        : 0

    if (upload.state === 'processing') {
        return (
            <div>
                <p>{t('processing_upload')}</p>
                <ProgressBar
                    percent={progressPercent}
                    label={`${upload.processed_count.toLocaleString()} / ${upload.row_count.toLocaleString()} ${t('rows')} (${progressPercent}%)`}
                />
            </div>
        )
    }

    if (upload.state === 'complete') {
        return (
            <div>
                <Alert variant="success" title={t('import_complete')}>
                    {upload.processed_count.toLocaleString()} {t('rows')} {t('imported')}
                </Alert>
                {upload.error && (
                    <Alert variant="warn" title={t('import_warnings')}>
                        {upload.error}
                    </Alert>
                )}
                <div className="upload-actions">
                    <Button onClick={() => onClose(true)}>
                        {t('done')}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div>
            <Alert variant="error" title={t('import_failed')}>
                {upload.error ?? t('unknown_error')}
            </Alert>
            <div className="upload-actions">
                <Button variant="secondary" onClick={() => onClose()}>
                    {t('close')}
                </Button>
            </div>
        </div>
    )
}

// --- Main Modal ---

export default function UserUploadModal({ open, onClose, listId }: UserUploadModalProps) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [step, setStep] = useState<Step>('upload')
    const [upload, setUpload] = useState<UserUpload | null>(null)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [isUploading, setIsUploading] = useState(false)
    const [mapping, setMapping] = useState<Record<string, string>>({})
    const [error, setError] = useState<string | undefined>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [sampleData, setSampleData] = useState<Array<Record<string, string>>>([])
    const [previewIndex, setPreviewIndex] = useState(0)
    const pollRef = useRef<ReturnType<typeof setInterval>>()

    useEffect(() => {
        if (open) {
            setStep('upload')
            setUpload(null)
            setUploadProgress(0)
            setIsUploading(false)
            setMapping({})
            setError(undefined)
            setIsSubmitting(false)
            setSampleData([])
            setPreviewIndex(0)
        }
        return () => {
            if (pollRef.current) clearInterval(pollRef.current)
        }
    }, [open])

    const handleUpload = async ({ file }: { file: FileList }) => {
        setIsUploading(true)
        setError(undefined)
        try {
            const result = await api.users.uploads.create(
                project.id,
                file[0],
                setUploadProgress,
            )
            const { sample_data, ...uploadData } = result as UserUpload & { sample_data?: Array<Record<string, string>> }
            setSampleData(sample_data ?? [])
            setUpload(uploadData)
            const autoMapping: Record<string, string> = {}
            for (const header of result.headers ?? []) {
                const lower = header.toLowerCase().trim()
                const match = RESERVED_FIELDS.find(f => f === lower)
                if (match) {
                    autoMapping[header] = match
                } else {
                    autoMapping[header] = ''
                }
            }
            setMapping(autoMapping)
            setStep('mapping')
        } catch (err: any) {
            setError(err.response?.data?.error ?? err.message)
        } finally {
            setIsUploading(false)
        }
    }

    const handleConfirmMapping = async () => {
        if (!upload) return
        setIsSubmitting(true)
        setError(undefined)
        try {
            const columnMapping: Record<string, string> = {}
            for (const [csvCol, target] of Object.entries(mapping)) {
                columnMapping[csvCol] = target || csvCol
            }

            await api.users.uploads.updateMapping(project.id, upload.id, {
                column_mapping: columnMapping,
                list_id: listId,
            })
            const result = await api.users.uploads.process(project.id, upload.id)
            setUpload(result)
            setStep('processing')
            startPolling()
        } catch (err: any) {
            setError(err.response?.data?.error ?? err.message)
        } finally {
            setIsSubmitting(false)
        }
    }

    const startPolling = () => {
        if (pollRef.current) clearInterval(pollRef.current)
        pollRef.current = setInterval(async () => {
            if (!upload) return
            try {
                const updated = await api.users.uploads.get(project.id, upload.id)
                setUpload(updated)
                if (updated.state === 'complete' || updated.state === 'failed') {
                    if (pollRef.current) clearInterval(pollRef.current)
                }
            } catch {
                // Ignore polling errors
            }
        }, 2000)
    }

    return (
        <Modal
            open={open}
            onClose={() => onClose()}
            title={t('import_users')}
            size="regular"
        >
            {error && <Alert variant="error" title={t('error')}>{error}</Alert>}

            {step === 'upload' && (
                <UploadStep
                    isUploading={isUploading}
                    uploadProgress={uploadProgress}
                    onUpload={handleUpload}
                />
            )}

            {step === 'mapping' && upload?.headers && (
                <MappingStep
                    upload={upload as UserUpload & { headers: string[] }}
                    project={project}
                    mapping={mapping}
                    sampleData={sampleData}
                    previewIndex={previewIndex}
                    isSubmitting={isSubmitting}
                    onMappingChange={(col, target) => setMapping(prev => ({ ...prev, [col]: target }))}
                    onPreviewPrev={() => setPreviewIndex(i => (i - 1 + sampleData.length) % sampleData.length)}
                    onPreviewNext={() => setPreviewIndex(i => (i + 1) % sampleData.length)}
                    onBack={() => { setStep('upload'); setUpload(null) }}
                    onConfirm={handleConfirmMapping}
                />
            )}

            {step === 'processing' && upload && (
                <ProcessingStep upload={upload} onClose={onClose} />
            )}
        </Modal>
    )
}
